import { localize } from 'mixins/helpers';
import { TableColumnsType } from 'antd';
import dayjs from 'dayjs';

interface ICertificateType {
    issueDate: string;
    issueOrg: string;
    name: string;
    number: string | number;
}

const getCertificationString = (certificate: ICertificateType): string => {
    if (!certificate) return '';

    const { issueDate, issueOrg, number, name } = certificate;

    const parsedNameString = name ? `${name},` : '';
    const parsedNumberString = number ? `${number},` : '';
    const parsedDateString = issueDate ? `${dayjs(issueDate).format('DD.MM.YYYY')},` : '';
    const parsedIssueString = issueOrg || '';

    return `${parsedNameString}${parsedNumberString}${parsedDateString}${parsedIssueString}`;
};

export const columns = (translations: Record<string, string>): TableColumnsType => [
    {
        title: localize('find-admin-fullname', translations),
        render: (item) =>
            `${item?.lastName || ''} ${item?.firstName || ''} ${item?.middleName || ''}`,
        width: 200,
    },
    {
        title: localize('find-admin-address', translations),
        dataIndex: 'address',
        key: 'address',
        width: 200,
    },
    {
        title: localize('find-admin-email', translations),
        dataIndex: 'email',
        key: 'email',
        width: 200,
    },
    {
        title: localize('find-admin-phone', translations),
        dataIndex: 'phone',
        key: 'phone',
        width: 200,
    },
    {
        title: localize('find-admin-date-of-inclusion-in-register', translations),
        dataIndex: 'beginDate',
        key: 'beginDate',
        width: 200,
    },
    {
        title: localize('find-admin-exp', translations),
        dataIndex: 'experience',
        key: 'experience',
        width: 250,
    },
    {
        title: localize('find-admin-debtors-exist-count-bankrupt', translations),
        dataIndex: 'existingRehabilitationCount',
        key: 'existingRehabilitationCount',
        width: 250,
    },
    {
        title: localize('find-admin-debtors-exist-count-rehab', translations),
        width: 200,
        children: [
            {
                title: localize('find-admin-all', translations),
                width: 100,
                render: (item) => item?.closedRehabilitation?.closedRehabilitationTotal,
            },
            {
                title: localize('find-admin-on-next-reasons', translations),
                children: [
                    {
                        title: localize('find-admin-reached-target-rehab', translations),
                        width: 300,
                        render: (item) =>
                            item?.closedRehabilitation?.achievedClosedRehabilitationCount,
                    },
                    {
                        title: localize('find-admin-creditor-rehab-money-more-than', translations),
                        width: 300,
                        render: (item) =>
                            item?.closedRehabilitation?.monetaryObligationsRehabilitationCount,
                    },
                    {
                        title: localize(
                            'find-admin-creditor-rehab-release-plan-damage',
                            translations
                        ),
                        width: 300,
                        render: (item) =>
                            item?.closedRehabilitation?.resultsCauseDamageRehabilitationCount,
                    },
                    {
                        title: localize(
                            'find-admin-creditor-rehab-release-plan-damage-with-director',
                            translations
                        ),
                        width: 300,
                        render: (item) =>
                            item?.closedRehabilitation?.actionsCauseDamagedRehabilitationCount,
                    },
                    {
                        title: localize(
                            'find-admin-creditor-rehab-release-plan-notified',
                            translations
                        ),
                        width: 300,
                        render: (item) =>
                            item?.closedRehabilitation?.notifiedClosedRehabilitationCount,
                    },
                ],
            },
        ],
    },
    {
        title: localize('find-admin-creditor-bankruptcy-existing-year', translations),
        width: 200,
        dataIndex: 'existingBankruptcyCount',
        key: 'existingBankruptcyCount',
    },
    {
        title: localize('find-admin-creditor-bankruptcy-existing-year-closed', translations),
        width: 200,
        children: [
            {
                title: localize('find-admin-all', translations),
                width: 150,
                render: (item) => item?.closedBankruptcy?.closedBankruptcyTotal,
            },
            {
                title: localize('find-admin-by-the-way-with-money', translations),
                width: 200,
                render: (item) => item?.closedBankruptcy?.includingPropertyBankruptcySum,
            },
            {
                title: localize('find-admin-from-them', translations),
                children: [
                    {
                        title: localize('find-admin-claim-money-sum', translations),
                        width: 200,
                        render: (item) => item?.closedBankruptcy?.amountReceivedBankruptcySum,
                    },
                    {
                        title: localize('find-admin-claim-money-sum-got', translations),
                        width: 300,
                        render: (item) => item?.closedBankruptcy?.istatedClaimsBankruptcySum,
                    },
                    {
                        title: localize('find-admin-claim-money-sum-sent', translations),
                        width: 300,
                        render: (item) => item?.closedBankruptcy?.amountAdministrativeExpensesSum,
                    },
                    {
                        title: localize('find-admin-claim-money-sum-claimed', translations),
                        width: 300,
                        render: (item) => item?.closedBankruptcy?.satisfiedClaimsBankruptcySum,
                    },
                ],
            },
        ],
    },
    {
        title: localize('find-admin-laws-count-result', translations),
        children: [
            {
                title: localize('find-admin-plb', translations),
                width: 300,
                render: (item) => item?.inspectionDTO?.notVisitingControlDocumentCount,
            },
            {
                title: localize('find-admin-plb-with', translations),
                width: 300,
                render: (item) => item?.inspectionDTO?.visitingControlDocumentCount,
            },
            {
                title: localize('find-admin-on-time-researches', translations),
                width: 200,
                render: (item) => item?.inspectionDTO?.unscheduledInspectionsControlCount,
            },
        ],
    },
    {
        title: localize('find-admin-penalty-by-year-count', translations),
        key: 'administrativePenaltiesCount',
        dataIndex: 'administrativePenaltiesCount',
        width: 200,
    },
    {
        title: localize('find-admin-qualifications-details', translations),
        children: [
            {
                title: localize('find-admin-certificate', translations),
                dataIndex: 'certificate',
                key: 'certificate',
                render: (certificate) => getCertificationString(certificate),
                width: 300,
            },
            {
                title: localize('find-admin-other-doc', translations),
                dataIndex: 'otherDocument',
                key: 'otherDocument',
                render: (certificate) => getCertificationString(certificate),
                width: 300,
            },
        ],
    },
];
